import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';

// -------------------- Job Mode --------------------
export type JobMode = 'service' | 'project';

// -------------------- Base Schema Fields --------------------
const baseJobSchema = z.object({
  // Customer & Property (required for both)
  customerId: z.number().min(1, 'Customer is required'),
  customerName: z.string().optional(),
  propertyId: z.number().min(1, 'Property is required'),
  propertyAddress: z.string().optional(),

  // Common fields
  name: z.string().min(1, 'Job name/description is required'),
  description: z.string().min(1, 'Service needed is required'),
  logNotes: z.string().optional(),
});

// -------------------- Service Call Schema --------------------
export const serviceCallSchema = baseJobSchema
  .extend({
    mode: z.literal('service'),

    // Department & Type
    departmentId: z.number().min(1, 'Department is required'),
    jobTypeId: z.number().min(1, 'Job Type is required'),
    jobSubtypeId: z.number().optional(),

    // Billing
    logPaymentType: z.string().min(1, 'Billing Type is required'),
    amountQuoted: z.string().optional(),

    // Scheduling
    dateStarted: z.string().min(1, 'Scheduled date is required'),
    timeStarted: z.string().optional(),
    timeFinished: z.string().optional(),

    // Job Status
    logJobStatus: z.string().min(1, 'Job Status is required'),

    // Flags
    isCallback: z.number().optional().default(0),
    callbackOriginalId: z.number().optional(),
    diagnosticQuoted: z.number().optional().default(0),
    highPriority: z.number().optional().default(0),
    isLmpc: z.number().optional().default(0),

    // Technicians
    technicianUserIds: z.array(z.number()).optional().default([]),

    // Service-specific
    logJobNumber: z.string().optional(),
    logPo: z.string().optional(),
  })
  .refine(
    (data) => {
      // If isCallback is checked, callbackOriginalId must be provided
      if (data.isCallback === 1 && (!data.callbackOriginalId || data.callbackOriginalId === 0)) {
        return false;
      }
      return true;
    },
    {
      message: 'Callback job must be selected when "Is Callback" is checked',
      path: ['callbackOriginalId'],
    },
  );

// -------------------- Project Schema --------------------
export const projectSchema = baseJobSchema
  .omit({ name: true }) // Remove 'name' from base schema since projects auto-generate the name
  .extend({
    mode: z.literal('project'),

    // Dates
    dateStarted: z.string().min(1, 'Start date is required'),
    dateEnded: z.string().min(1, 'End date is required'),

    // Contract
    contractNumber: z.string().optional(),
    logPo: z.string().optional(),

    // Department Assignments (can be empty initially, budget added later)
    departmentAssignments: z
      .array(
        z.object({
          departmentId: z.number(),
          departmentName: z.string().optional(),
          workType: z.enum(['internal', 'sub']).default('internal'),
          allocatedBudget: z.number().optional().default(0),
          allocatedHours: z.number().optional().default(0),
        }),
      )
      .min(1, 'At least one department must be selected'),

    // Prime Department (represents the primary event ID)
    primeDepartmentId: z.number().min(1, 'Prime department is required'),

    // Project-specific fields
    logJobNumber: z.string().optional(),
  })
  .refine(
    (data) => {
      // Validate that end date is after start date (not equal, not before)
      // YYYY-MM-DD format can be compared directly as strings
      if (data.dateStarted && data.dateEnded) {
        return data.dateEnded > data.dateStarted;
      }
      return true;
    },
    {
      message: 'End date must be after start date (cannot be the same day)',
      path: ['dateEnded'],
    },
  );

// -------------------- Unified Schema --------------------
// Using union instead of discriminated union to avoid validation issues
export const unifiedJobCreateSchema = z.union([serviceCallSchema, projectSchema]);

export type UnifiedJobCreateScheme = z.infer<typeof unifiedJobCreateSchema>;
export type ServiceCallScheme = z.infer<typeof serviceCallSchema>;
export type ProjectScheme = z.infer<typeof projectSchema>;

// -------------------- Default Values --------------------
export const getDefaultServiceCallValues = (): ServiceCallScheme => ({
  mode: 'service',
  customerId: 0,
  customerName: '',
  propertyId: 0,
  propertyAddress: '',
  name: '',
  description: '',
  logNotes: '',
  departmentId: 0,
  jobTypeId: 0,
  jobSubtypeId: 0,
  logPaymentType: '',
  amountQuoted: '',
  dateStarted: new Date().toISOString().split('T')[0],
  timeStarted: '',
  timeFinished: '',
  logJobStatus: 'Confirmed',
  isCallback: 0,
  callbackOriginalId: 0,
  diagnosticQuoted: 0,
  highPriority: 0,
  isLmpc: 0,
  technicianUserIds: [],
  logJobNumber: '',
  logPo: '',
});

export const getDefaultProjectValues = (): ProjectScheme => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return {
    mode: 'project',
    customerId: 0,
    customerName: '',
    propertyId: 0,
    propertyAddress: '',
    description: '',
    logNotes: '',
    dateStarted: today.toISOString().split('T')[0],
    dateEnded: tomorrow.toISOString().split('T')[0],
    contractNumber: '',
    logPo: '',
    departmentAssignments: [],
    primeDepartmentId: 0,
    logJobNumber: '',
  };
};

// -------------------- Form Hook --------------------
export const useUnifiedJobCreateForm = (mode: JobMode = 'service') => {
  const defaultValues =
    mode === 'service' ? getDefaultServiceCallValues() : getDefaultProjectValues();

  return useForm<UnifiedJobCreateScheme>({
    resolver: zodResolver(unifiedJobCreateSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });
};

export const useUnifiedJobCreateFormContext = () => {
  return useFormContext<UnifiedJobCreateScheme>();
};
